import { Banner, BannerState, BannerType } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { renderTemplate } from '@/modules/adv/providers/google-banner/template'
import { AdProviderNames, PlatformType } from '@/types'
import { loadScript } from '@/utils/loadScript'

import {
    AdProvider,
    type AdProviderConfig,
    AdProviderStatus,
    type AdUnitConfig,
    NotImplementedError,
    type OutOfPageAdType,
    type PageAdType,
} from '../../'

export const GAM_SOURCE = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

export class GoogleBannerProvider implements AdProvider {
    private readonly gpt: typeof googletag

    private readonly banners = new Map<googletag.Slot, Banner | OutOfPageBanner>()

    private readonly gamAccount: string

    private readonly adUnitConfig: AdUnitConfig | undefined

    private readonly useOnlyConfig: boolean

    private readonly platform: PlatformType

    private outOfPageBannerRenderTimeout: number | undefined

    private readonly footerHeight: string

    private currentTargeting: Record<string, string | string[]> = {}

    readonly serviceStatus: Promise<AdProviderStatus>

    constructor({
        targeting = {},
        gamAccount,
        platform,
        auid,
        adUnitConfig,
        useOnlyConfig,
        footerHeight = '0px',
    }: AdProviderConfig & { gamAccount: string; footerHeight?: string }) {
        window.googletag = window.googletag || { cmd: [] }
        window.googletag.cmd = window.googletag.cmd || [] // it may be not empty after reload with cached resources

        this.gpt = window.googletag

        this.gamAccount = gamAccount
        this.footerHeight = footerHeight
        this.platform = platform
        this.adUnitConfig = adUnitConfig
        this.useOnlyConfig = !!adUnitConfig && !!useOnlyConfig

        this.serviceStatus = new Promise((resolve, reject) => {
            window.setTimeout(
                () => {
                    loadScript({
                        src: GAM_SOURCE,
                        useExisting: true,
                        crossorigin: 'anonymous',
                    }).catch(() => reject(AdProviderStatus.offline))
                },
                // in dev mode the script is inserted by HeadMeta a bit later, so just wait a sec
                process.env.NODE_ENV === 'production' ? 0 : 1000,
            )

            this.updateTargeting(targeting)
            this.gpt.cmd.push(() => {
                this.gpt.pubads().setPublisherProvidedId(auid)
                this.gpt.setConfig({
                    centering: true,
                    adsenseAttributes: {
                        page_url: 'playgama.com',
                    },
                })

                this.gpt.pubads().addEventListener('impressionViewable', (event) => {
                    const banner = this.banners.get(event.slot)

                    if (banner?.type === BannerType.outOfPageBanner) {
                        const b = banner as OutOfPageBanner
                        b.triggerReady(() => {
                            b.triggerRendered()
                            window.clearTimeout(this.outOfPageBannerRenderTimeout)
                            this.outOfPageBannerRenderTimeout = undefined
                            b.triggerViewable()
                        })
                    }
                })

                this.gpt.pubads().addEventListener('slotRenderEnded', (event) => {
                    const banner = this.banners.get(event.slot)

                    if (event.isEmpty) {
                        return banner?.triggerEmpty()
                    }
                })

                this.gpt.enableServices()
                resolve(AdProviderStatus.online)
            })
        })

        window.addEventListener('resize', this.onViewportResize.bind(this))
    }

    // Since Google Ad doesn't seem to offer a proper way to handle viewport resizes gracefully,
    // we close the banner on the first resize event to avoid layout issues.
    private onViewportResize(): void {
        const banner = this.getOutOfPageBanner()
        if (!banner) {
            return
        }

        if (banner.state === BannerState.showing) {
            banner.triggerClosed()
        } else {
            banner.triggerEmpty()
        }
    }

    private getOutOfPageBanner(): OutOfPageBanner | null {
        for (const b of this.banners.values()) {
            if (b.type === BannerType.outOfPageBanner) {
                return b as OutOfPageBanner
            }
        }
        return null
    }

    private getOutOfPageBannerSizes(node: HTMLDivElement): googletag.GeneralSize {
        const sizes =
            this.platform === 'desktop'
                ? [
                      [300, 300],
                      [300, 250],
                      [336, 280],
                      [480, 320],
                      [300, 600],
                      [728, 90],
                      [300, 500],
                      [320, 480],
                  ]
                : [
                      [300, 250],
                      [300, 500],
                      [320, 480],
                      [300, 300],
                      [336, 280],
                  ]

        const width = node.clientWidth
        const height = node.clientHeight - 40 // 40 is the height of the top bar with the close button

        return sizes.filter((s) => s[0] <= width && s[1] <= height) as googletag.GeneralSize
    }

    public prepareOutOfPageAd(type: OutOfPageAdType, timeout = 1000): Promise<OutOfPageBanner> {
        const adUnit = this.getAdUnitByType(type as unknown as PageAdType)

        return new Promise((resolve, reject) => {
            this.gpt.cmd.push(() => {
                const adBlockId = crypto.randomUUID()
                const { wrapper, startCountdown, stopCountdown, setOnClose } = renderTemplate(
                    type,
                    this.footerHeight,
                    adBlockId,
                )

                const slot = this.gpt.defineSlot(adUnit, this.getOutOfPageBannerSizes(wrapper), adBlockId)

                if (!slot) {
                    reject(new Error('Failed to define banner slot'))
                    return
                }

                slot.addService(this.gpt.pubads())

                const banner = new OutOfPageBanner({
                    provider: AdProviderNames.GOOGLE_BANNER,
                    adUnit,
                    destroy: () => {
                        this.banners.delete(slot)
                        this.gpt.destroySlots([slot])
                        stopCountdown()
                        wrapper.remove()
                    },
                })

                this.outOfPageBannerRenderTimeout = window.setTimeout(() => {
                    banner?.triggerEmpty()
                    this.outOfPageBannerRenderTimeout = undefined
                }, timeout)

                this.banners.set(slot, banner)

                this.gpt.display(adBlockId)

                banner.addEventListener('rendered', () => {
                    wrapper.style.opacity = '1'
                    startCountdown((sec) => {
                        banner.triggerTimer(sec)
                    })
                })

                setOnClose(() => {
                    banner.triggerClosed()
                })

                banner.addEventListener('closed', banner.destroy)
                banner.addEventListener('empty', banner.destroy)

                resolve(banner)
            })
        })
    }

    // eslint-disable-next-line class-methods-use-this
    public requestPageAd(): Promise<Banner> {
        return Promise.reject(new NotImplementedError())
    }

    public updateTargeting(targeting: AdProviderConfig['targeting'] = {}) {
        for (const key in targeting) {
            if (targeting[key]) {
                this.currentTargeting[key] = targeting[key]
            } else {
                delete this.currentTargeting[key]
            }
        }

        this.gpt.cmd.push(() => {
            this.gpt.setConfig({
                targeting: this.currentTargeting,
            })
        })
    }

    public getAdUnitByType(type: OutOfPageAdType | PageAdType): string {
        const simplifiedPlatform = this.platform === 'ios' || this.platform === 'android' ? 'mobile' : 'desktop'

        const unitFromConfig = this.adUnitConfig?.[simplifiedPlatform]?.[type]
        if (unitFromConfig) {
            return `${this.gamAccount}/${unitFromConfig}`
        }

        if (this.useOnlyConfig) {
            throw new Error('No such ad unit in this provider')
        }

        const baseUnits: Record<string, string> = {
            interstitial_preroll: 'banner_ingame_preroll',
            interstitial: 'banner_ingame',
            rewarded: 'banner_ingame_rewarded',
        }

        const base = baseUnits[type]

        if (!base) {
            throw new NotImplementedError()
        }

        return `${this.gamAccount}/${base}_${simplifiedPlatform}`
    }
}
